import React, { Fragment, useMemo } from "react"
import { Avatar, Box, Typography, useTheme } from "@mui/material"
import _ from "lodash"
import { Link } from "react-router-dom"

import { getIcon } from "utils/icon"
import { IOccupantDetail, getFeatureDetail } from "utils/feature"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useNewShops } from "hooks/new-shop/useNewShops"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { EmptyData } from "components/EmptyData"
import { ImageCard } from "components/ImageCard"
import { MotionDiv } from "components/motions"
import { NavigationControl } from "components/kioskHorizontal/navigation"
import { PageCard, PageCardContent } from "components/card"
import { VenueSelector } from "components/VenueSelector"
import { StorefrontIcon } from "components/icons"

export const NewShopsKioskHPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
}) => {
  const { occupants, isMultiOrdinalVenue } = useVenue()
  const {
    newOccupants,
    comingSoonOccupants,
    hasData,
    isMultiVenueProject,
    venueFilter,
    venues,
    filterByVenueId,
    locationVenueId,
  } = useNewShops(occupants)

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.newShops) || <StorefrontIcon />,
    [theme]
  )
  const { t } = useTranslation()

  const { styleOverrides = {} } = theme.components?.[`NewShopsPage`] || {}
  const { styleOverrides: bannerStyleOverrides = {} } =
    theme.components[`AppBanner`] || {}
  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}

  const {
    pageCard,
    contentWrapper,
    gridContainer,
    venueSelector,
    emptyDataWrapper,
    // New shop styles
    newShopAvatarFallBack,
    newShopImageCard,
    newShopAvatar,
    newShopFallbackImageIconWrapper,
    newShopFallbackImageIcon,
    // Coming soon shop styles
    comingSoonShopAvatarFallBack,
    comingSoonShopAvatar,
    comingSoonShopImageCard,
    comingSoonShopFallbackImageIconWrapper,
    comingSoonShopFallbackImageIcon,
  } = styleOverrides

  const { root } = bannerStyleOverrides
  const { wrapper, body, footer } = sideBarStyleOverrides

  return (
    //@ts-ignore
    <Layout
      aside={
        <PageCard>
          <Box sx={wrapper}>
            <Box sx={root}></Box>
            <Box sx={body}>{AppNavigation && <AppNavigation />}</Box>
            <Box sx={footer}>
              <NavigationControl BackButtonProps={{ hidden: true }} />
            </Box>
          </Box>
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent>
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            sx={contentWrapper}
          >
            {hasData ? (
              <Box sx={gridContainer} className={hasData ? "" : "emptyList"}>
                {newOccupants.map((newOccupant, index) => {
                  const {
                    group,
                    level,
                    logo,
                    featuredImage,
                    name,
                    boothName,
                    categoryIcon,
                    subCategory,
                  } = (getFeatureDetail(newOccupant) as IOccupantDetail) || {}
                  const levelText = isMultiOrdinalVenue
                    ? t("Level Text", { level: t(level) })
                    : ""
                  const groupText = t(group)
                  return (
                    <ImageCard
                      variant="full-image"
                      key={`new-shop-${newOccupant.id}`}
                      sx={newShopImageCard}
                      component={Link}
                      to={`/maps/place/${newOccupant?.id}`}
                      title={t(name)}
                      subheader={
                        <>
                          <Typography
                            component="span"
                            //@ts-ignore
                            variant="subtitle"
                            className="sub-category-text"
                          >
                            {t(subCategory)}
                          </Typography>
                          {boothName && (
                            <Typography
                              component="span"
                              //@ts-ignore
                              variant="subtitle"
                              className="booth-name-text"
                            >
                              {`${t("Booth")} ${t(boothName)}`}
                            </Typography>
                          )}
                          <Typography
                            className="location-text"
                            component="span"
                            //@ts-ignore
                            variant="subtitle"
                          >
                            {_.compact([levelText, groupText]).join(" ")}
                          </Typography>
                        </>
                      }
                      image={featuredImage}
                      fallbackImageIcon={
                        <Box sx={newShopFallbackImageIconWrapper}>
                          {getIcon(categoryIcon, newShopFallbackImageIcon, {
                            imageIconProps: {
                              solidColorFilterId: "newShop_cover_filter",
                            },
                          })}
                        </Box>
                      }
                      avatar={
                        <Avatar src={logo} sx={newShopAvatar}>
                          {getIcon(categoryIcon, newShopAvatarFallBack, {
                            imageIconProps: {
                              solidColorFilterId: "newShop_avatar_filter",
                            },
                          })}
                        </Avatar>
                      }
                      tag={t("New Shop")}
                    />
                  )
                })}
                {comingSoonOccupants.map((comingSoonOccupant, index) => {
                  const {
                    group,
                    level,
                    logo,
                    featuredImage,
                    name,
                    boothName,
                    categoryIcon,
                    subCategory,
                  } =
                    (getFeatureDetail(comingSoonOccupant) as IOccupantDetail) ||
                    {}

                  const levelText = isMultiOrdinalVenue
                    ? t("Level Text", { level: t(level) })
                    : ""
                  const groupText = t(group)

                  return (
                    <ImageCard
                      variant="full-image"
                      key={`coming-soon-${comingSoonOccupant.id}`}
                      sx={comingSoonShopImageCard}
                      component={Link}
                      title={t(name)}
                      subheader={
                        <>
                          <Typography
                            component="span"
                            //@ts-ignore
                            variant="subtitle"
                            className="sub-category-text"
                          >
                            {t(subCategory)}
                          </Typography>
                          {boothName && (
                            <Typography
                              component="span"
                              //@ts-ignore
                              variant="subtitle"
                              className="booth-name-text"
                            >
                              {`${t("Booth")} ${t(boothName)}`}
                            </Typography>
                          )}
                          <Typography
                            className="location-text"
                            component="span"
                            //@ts-ignore
                            variant="subtitle"
                          >
                            {_.compact([levelText, groupText]).join(" ")}
                          </Typography>
                        </>
                      }
                      image={featuredImage}
                      fallbackImageIcon={
                        <Box sx={comingSoonShopFallbackImageIconWrapper}>
                          {getIcon(
                            categoryIcon,
                            comingSoonShopFallbackImageIcon,
                            {
                              imageIconProps: {
                                solidColorFilterId: "comingShop_cover_filter",
                              },
                            }
                          )}
                        </Box>
                      }
                      avatar={
                        <Avatar src={logo} sx={comingSoonShopAvatar}>
                          {getIcon(categoryIcon, comingSoonShopAvatarFallBack, {
                            imageIconProps: {
                              solidColorFilterId: "comingShop_avatar_filter",
                            },
                          })}
                        </Avatar>
                      }
                      tag={t("Open Soon")}
                    />
                  )
                })}
              </Box>
            ) : (
              <Box sx={emptyDataWrapper}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("Coming soon")}
                  secondary={t("Check Back Soon")}
                />
              </Box>
            )}

            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={filterByVenueId}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
          </MotionDiv>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
